package org.matthewo.pages.event.tabs

import emotion.react.css
import js.objects.buildReadonlyRecord
import org.matthewo.YEAR_PARAM
import org.matthewo.api.*
import org.matthewo.pages.event.EVENT_CODE_PARAM
import org.matthewo.pages.event.EVENT_PATH
import org.matthewo.pages.event.EventTabFC
import org.matthewo.pages.event.tabs.games.DecodeMatchResult
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.router.div
import org.matthewo.routes.API
import org.matthewo.schema.StandardMetrics
import org.matthewo.schema.namesUnsafe
import org.matthewo.styles.FONT_COLOR
import org.matthewo.styles.TABLE_HEAD_BLUE
import org.matthewo.styles.TABLE_HEAD_RED
import org.matthewo.styles.color
import org.matthewo.symbolprocessor.Period
import org.matthewo.useDefaultHeader
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useMemo
import tanstack.react.router.Link
import tanstack.router.core.ParamName
import tanstack.router.core.RoutePath
import tanstack.router.core.ToOptions
import web.cssom.*
import kotlin.reflect.KProperty1

val MATCH_RESULT_ID_PARAM = ParamName("id")

val EVENT_MATCH_RESULT_PATH = RoutePath(EVENT_PATH / "match" / MATCH_RESULT_ID_PARAM)

fun ToOptions.toEventMatchResult(year: Int? = null, eventCode: String? = null, matchAbbreviation: String, matchSeries: Int, matchNumber: Int) {
    to = EVENT_MATCH_RESULT_PATH
    params = buildReadonlyRecord {
        if (null != year) this[YEAR_PARAM] = year.toString()
        if (null != eventCode) this[EVENT_CODE_PARAM] = eventCode
        this[MATCH_RESULT_ID_PARAM] = "$matchAbbreviation$matchSeries-$matchNumber"
    }
}

fun ToOptions.toEventMatchResult(year: Int? = null, eventCode: String? = null, match: Schedule.Match) =
    toEventMatchResult(year, eventCode, match.level.abbreviation, match.series, match.number)

fun ToOptions.toEventMatchResult(year: Int? = null, eventTeamMatch: EventTeamMatch) = toEventMatchResult(
    year, eventTeamMatch.code,
    eventTeamMatch.level.abbreviation, eventTeamMatch.series, eventTeamMatch.number
)


val EventMatchResult = EventTabFC<API.Event.Match, EventMatch<MetricsAPI<Short>>>(
    { event, props -> API.Event.Match(event, props[MATCH_RESULT_ID_PARAM]) }
) { (coroutineScope, year, eventCode, data) ->
    useDefaultHeader()

    val meta = year?.gameMeta()

    @Suppress("UNCHECKED_CAST")
    val metrics = useMemo(meta) {
        if (null == meta) return@useMemo []
        val namedMetricPeriods = meta.periods.mapKeys { it.key.name }
        meta.namesUnsafe<Short, StandardMetrics<Short>>().entries.mapNotNull { (metric, name) ->
            namedMetricPeriods[metric.name]?.let { period ->
                MetricInfo(
                    metric as KProperty1<MetricsAPI<Short>, Number>,
                    period,
                    name
                )
            }
        }
    }

    div {
        css {
            display = Display.grid
            gridTemplateColumns = repeat(2, 1.fr)
            textAlign = TextAlign.center
            position = Position.sticky
            top = 0.px
        }
        [TABLE_HEAD_RED to data?.redTeams, TABLE_HEAD_BLUE to data?.blueTeams].forEach { (allianceColor, teams) ->
            div {
                css {
                    backgroundColor = allianceColor
                    display = Display.grid
                    gridAutoFlow = GridAutoFlow.column
                    gridAutoColumns = 1.fr
                }
                teams?.forEach { team ->
                    Link {
                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            padding = 0.5.em
                            color = FONT_COLOR
                            textDecoration = None.none
                        }

                        toTeam(year, team.number, eventCode)

                        span {
                            css {
                                fontSize = 1.5.em
                                color = if (team.onField) FONT_COLOR else NamedColor.gray
                                if (team.dq) {
                                    textDecoration = TextDecoration.lineThrough
                                    textDecorationThickness = 0.15.em
                                }
                            }
                            span {
                                css {
                                    textDecoration = TextDecoration.underline
                                    textDecorationThickness = Globals.unset
                                }
                                +"${team.number}${if (team.surrogate) "*" else ""}"
                            }
                        }
                        div {
                            +team.name
                        }
                    }
                }
            }
        }
    }

    Period.entries.forEach { p ->
        metrics.filter { it.period == p }.forEachIndexed { i, (metric, period, name) ->
            val header = 0 == i
            div {
                css {
                    background = period.color().run { if (header) tableHead else tableBody }
                    display = Display.grid
                    gridTemplateColumns = repeat(3, 1.fr)
                    textAlign = TextAlign.center
                    padding = 0.5.em
                }
                div {
                    data?.redResult?.let { +metric(it).toInt().toString() }
                }
                div {
                    css {
                        fontWeight = FontWeight.bold
                    }
                    +name
                }
                div {
                    data?.blueResult?.let { +metric(it).toInt().toString() }
                }
            }
        }
    }

    val redResult = data?.redResult
    val blueResult = data?.blueResult
    when {
        redResult is MetricsDecodeAPI && blueResult is MetricsDecodeAPI -> DecodeMatchResult {
            red = redResult
            blue = blueResult
        }
    }
}

private data class MetricInfo<M>(val metric: KProperty1<M, Number>, val period: Period, val name: String)
